type APIError = Error | BackEndError | unknown;

type BackEndError = {
  success: boolean;
  code: number | string;
  description: string;
};

export type NormalisedError = BackEndError;

type QueryParams = Record<string, string | number | boolean>;

type RequestParams = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  queryParams?: QueryParams;
};

const buildURL = (url: string, { queryParams }: RequestParams): string => {
  const finalURL = new URL(url);

  if (queryParams && Object.keys(queryParams).length !== 0) {
    Object.entries(queryParams).forEach(([key, value]) => {
      finalURL.searchParams.append(key, String(value));
    });
  }

  return finalURL.toString();
};

const buildFetchRequest = ({
  method = 'GET',
  body = {},
  headers = {},
}: RequestParams): RequestInit => {
  const requestHeaders: HeadersInit = buildHeaders(headers);
  const requestConfig: RequestInit = { method, headers: requestHeaders };

  if (method !== 'GET') {
    requestConfig.body = JSON.stringify(body);
  }

  return requestConfig;
};

const buildHeaders = ({
  headers: customHeaders,
}: RequestParams): HeadersInit => {
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  return {
    ...defaultHeaders,
    ...customHeaders,
  };
};

const handleAPIError = (error: APIError): NormalisedError => {
  if (error instanceof Error) {
    return { success: false, description: error.message, code: 500 };
  }

  if (typeof error === 'object' && error !== null) {
    const backEndError = error as BackEndError;
    // error from backend
    return {
      success: false,
      description: backEndError?.description || 'An unknown error occurred',
      code: backEndError?.code || 500,
    };
  }

  return { success: false, description: String(error), code: 500 };
};

export const fetchRequest = async <TResponse>(
  endpoint: string,
  requestParams: RequestParams
): Promise<TResponse> => {
  try {
    const finalURL = buildURL(endpoint, requestParams);
    const requestConfig = buildFetchRequest(requestParams);

    const response = await fetch(finalURL, requestConfig);

    if (response.status === 204) {
      // Return null for 204 No Content
      return null as TResponse;
    }

    const result = await response.json();

    if (!response.ok) {
      throw result;
    }

    return result as TResponse;
  } catch (error) {
    throw handleAPIError(error);
  }
};

export default fetchRequest;
